import {useEffect, useMemo, useRef, useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {AlertCircle, CheckCircle2, Film, Pencil, XCircle} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Spinner,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {type ApproveQcReviewPayload, type QcReviewItem} from '@/api/qcReview';
import {type Classification} from '@/api/classifications';
import {
  useApproveQcReview,
  useRejectQcReview,
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {ItemMeta} from '@/components/shared/ItemMeta';
import {useTranslatedText} from '@/store/translationStore';
import {ClassificationTree} from '@/components/classify/ClassificationTree';
import {EnrichmentForm, type EnrichmentValues} from '@/components/classify/EnrichmentForm';
import {riskVariant} from '@/lib/status';
import {discouragesScreenshots} from '@/components/classify/NoteScreenshots';
import {AnalystSubmissionDetails} from '@/components/shared/AnalystSubmissionDetails';
import {ReviewCalibrationNotes} from '@/components/submissions/CalibrationSection';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {showToast} from '@/lib/toast';

type Props = {
  readonly item: QcReviewItem | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmitted?: () => void;
  /**
   Opens straight into the correction picker - set when the caller's entry point was specifically
   "Approve with edits", so the lead doesn't have to click through to reveal it.
   */
  readonly startInEditMode?: boolean;
  /** When reviewing sequentially ("Review all"), shows "N of M" progress in the header. */
  readonly queuePosition?: {current: number; total: number} | undefined;
};

/**
 The analyst's own attributes as the enrichment form's values - the baseline an edit is measured
 against, so an untouched field is never sent as a correction.
 */
function enrichmentFrom(item: QcReviewItem): EnrichmentValues {
  return {
    platform: item.platform,
    submissionType: item.submission.submission_type === 'Actor' ? 'Actor' : 'Content',
    notes: item.submission.notes ?? '',
    exampleVideoIds: item.submission.example_ids ?? '',
    languages: item.submission.language,
    topics: item.submission.topic,
    locations: item.submission.location,
    coreRisks: item.submission.core_risk
  };
}

function sameSet(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/**
 The risk node an item currently sits under - the one whose ancestry reaches a policy violation of
 the same name. Drives which Core Risks the enrichment form offers before any correction is picked.
 */
function findRiskNode(
  classifications: Classification[],
  policyViolation: string,
  risk: string
): Classification | null {
  const byId = new Map(classifications.map((node) => [node.id, node]));
  const candidates = classifications.filter((node) => node.type === 'risk' && node.name === risk);
  const underPolicyViolation = candidates.find((node) => {
    let parent = node.parentId ? byId.get(node.parentId) : undefined;
    while (parent) {
      if (parent.type !== 'risk' && parent.name === policyViolation) {
        return true;
      }

      parent = parent.parentId ? byId.get(parent.parentId) : undefined;
    }

    return false;
  });
  return underPolicyViolation ?? candidates[0] ?? null;
}

/**
 Full-detail review for a QC item - video preview and context, with Approve, Reject, and an
 optional correction to any attribute the analyst supplied (policy violation/risk, notes, example
 ids, language, location, topics, core risks) all in one place. Rejecting always requires notes;
 approving doesn't, whether or not a correction is attached. Only fields that actually differ from
 the analyst's are sent, so the server records exactly what the reviewer changed.
 */
export function QcReviewModal({
  item,
  onOpenChange,
  onSubmitted,
  startInEditMode,
  queuePosition
}: Props) {
  const approve = useApproveQcReview();
  const reject = useRejectQcReview();
  const {data: classifications = []} = useClassificationConfig();
  const [showCorrection, setShowCorrection] = useState(false);
  const [correction, setCorrection] = useState<{
    vertical: Classification;
    risk: Classification;
  } | null>(null);
  const [enrichment, setEnrichment] = useState<EnrichmentValues | null>(null);
  const {data: languages = []} = useLanguages();
  const {data: topics = []} = useTopics();
  const {data: locations = []} = useLocations();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!item) {
      return;
    }

    setNotes('');
    setError(null);
    setCorrection(null);
    setEnrichment(enrichmentFrom(item));
    setShowCorrection(Boolean(startInEditMode));
  }, [item, startInEditMode]);

  const currentRisk = useMemo(
    () =>
      item
        ? findRiskNode(classifications, item.policy_violation, item.submission.risks[0] ?? '')
        : null,
    [classifications, item]
  );
  // QC review edits one risk, so there is at most one Core Risk picker - the corrected node's
  // mappings when the reviewer has re-classified, otherwise the submission's own.
  const qcCoreRiskFields = useMemo(() => {
    const risk = correction?.risk ?? currentRisk;
    const options = risk?.coreRisks ?? [];
    return options.length > 0 ? [{label: risk!.displayName, options}] : [];
  }, [correction, currentRisk]);
  const edits = useMemo<ApproveQcReviewPayload>(
    () => (item && enrichment ? diffEdits(item, enrichment) : {}),
    [item, enrichment]
  );
  const hasEdits = correction !== null || Object.keys(edits).length > 0;

  const translatedTitle = useTranslatedText(item?.id ?? '', item?.title);
  const notesMissing = notes.trim() === '';
  const isPending = approve.isPending || reject.isPending;

  async function handleApprove() {
    if (!item || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setError(null);
    try {
      await approve.mutateAsync({
        id: item.id,
        payload: {
          ...(showCorrection ? edits : {}),
          ...(correction
            ? {policy_violation: correction.vertical.name, risk: correction.risk.name}
            : {}),
          ...(notes.trim() === '' ? {} : {notes: notes.trim()})
        }
      });
      showToast('Approved.');
      onSubmitted?.();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to approve item.'));
    } finally {
      submittingRef.current = false;
    }
  }

  async function handleReject() {
    if (!item || notesMissing || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setError(null);
    try {
      await reject.mutateAsync({id: item.id, payload: {notes: notes.trim()}});
      showToast('Rejected.');
      onSubmitted?.();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to reject item.'));
    } finally {
      submittingRef.current = false;
    }
  }

  return (
    <Dialog open={item !== null} onOpenChange={onOpenChange}>
      {/* Always as tall as the dialog may be, rather than as tall as its content: the size stays put
          as Review all steps from item to item, and an odd content height can't centre the dialog on
          a half pixel, which antialiased its edges one of two ways and flaked the screenshot test. */}
      <DialogContent className="h-[calc(100dvh-4rem)] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review QC item
            {queuePosition ? (
              <Badge variant="secondary">{`${queuePosition.current} of ${queuePosition.total}`}</Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {item ? (
              <>
                <span>{item.analyst_email}</span>
                <span aria-hidden="true">·</span>
                <span>
                  Created {formatDistanceToNow(new Date(item.created_at), {addSuffix: true})}
                </span>
                <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {/*
          The body is the one part that scrolls, so the verdict buttons in the sticky footer stay in
          view however long the item's metadata and the correction picker run.
        */}
        <DialogBody>
          {item ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-3">
                <div className="group bg-muted relative aspect-video w-full overflow-hidden">
                  {item.thumbnail_url ? (
                    <SensitiveMedia
                      src={item.thumbnail_url}
                      alt={item.title ?? 'Item preview'}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2">
                      <Film className="size-8" />
                      <span className="text-xs">No preview available</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <h3 className="font-heading text-sm leading-snug font-medium">
                    {translatedTitle ?? item.item_external_id ?? item.id}
                  </h3>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                    <span>{item.author_name ?? item.author_handle ?? '—'}</span>
                    <Badge variant="outline" className="uppercase">
                      {item.platform}
                    </Badge>
                    {item.published_at ? (
                      <span>
                        {formatDistanceToNow(new Date(item.published_at), {addSuffix: true})}
                      </span>
                    ) : null}
                  </div>
                  {item.url ? <CopyableUrl url={item.url} className="text-xs" /> : null}
                </div>

                <ItemMeta metadata={item.metadata} itemKey={item.id} title={item.title} />
              </div>

              <div className="flex min-w-0 flex-col gap-4">
                <FieldSet className="gap-0">
                  <FieldLegend variant="label">Analyst classification</FieldLegend>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline">{item.policy_violation}</Badge>
                    <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
                    <Badge variant="secondary">{item.submission.submission_type}</Badge>
                  </div>
                </FieldSet>

                <AnalystSubmissionDetails
                  submission={item.submission}
                  classificationId={item.classification_id}
                />

                {showCorrection ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">Corrected classification</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          setShowCorrection(false);
                          setCorrection(null);
                          setEnrichment(enrichmentFrom(item));
                        }}>
                        Discard edits
                      </Button>
                    </div>

                    {correction ? (
                      <div className="border-border flex items-center justify-between gap-2 rounded-md border p-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline">{correction.vertical.displayName}</Badge>
                          <Badge>{correction.risk.displayName}</Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setCorrection(null);
                          }}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <ClassificationTree
                        classifications={classifications}
                        onSelect={(vertical, risk) => {
                          setCorrection({vertical, risk});
                        }}
                      />
                    )}

                    {enrichment ? (
                      <EnrichmentForm
                        discourageScreenshots={discouragesScreenshots([
                          correction?.vertical.name ?? item.policy_violation
                        ])}
                        coreRiskFields={qcCoreRiskFields}
                        values={enrichment}
                        languages={languages}
                        topics={topics}
                        locations={locations}
                        onChange={setEnrichment}
                      />
                    ) : null}
                  </div>
                ) : null}

                <Field>
                  <FieldLabel htmlFor="qc-review-notes">Review notes</FieldLabel>
                  <Textarea
                    id="qc-review-notes"
                    value={notes}
                    placeholder="Explain why this shouldn't be delivered…"
                    onChange={(event) => {
                      setNotes(event.target.value);
                    }}
                  />
                  <FieldDescription>
                    Required to reject; optional feedback when approving.
                  </FieldDescription>
                </Field>

                <ReviewCalibrationNotes submissionId={item.submission_id} />

                {error ? (
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>{error}</AlertTitle>
                  </Alert>
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter sticky>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}>
            Cancel
          </Button>
          {item && !showCorrection ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCorrection(true);
              }}>
              <Pencil />
              Approve with edits…
            </Button>
          ) : null}
          <Button
            type="button"
            variant="destructive"
            disabled={notesMissing || isPending}
            onClick={() => void handleReject()}>
            {reject.isPending ? <Spinner /> : <XCircle />}
            Reject
          </Button>
          <Button type="button" disabled={isPending} onClick={() => void handleApprove()}>
            {approve.isPending ? <Spinner /> : <CheckCircle2 />}
            {showCorrection && hasEdits ? 'Approve with edits' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 Only the attributes the reviewer actually changed, in the approve payload's shape. The server also
 ignores a value equal to the current one, but not sending it keeps the request an honest record.
 */
function diffEdits(item: QcReviewItem, values: EnrichmentValues): ApproveQcReviewPayload {
  const payload: ApproveQcReviewPayload = {};
  const enrichment: NonNullable<ApproveQcReviewPayload['enrichment']> = {};
  if (values.notes.trim() !== (item.submission.notes ?? '').trim()) {
    payload.submission_notes = values.notes.trim();
  }

  if (values.exampleVideoIds.trim() !== (item.submission.example_ids ?? '').trim()) {
    payload.example_ids = values.exampleVideoIds.trim();
  }

  if (!sameSet(values.languages, item.submission.language)) {
    enrichment.language = values.languages;
  }

  if (!sameSet(values.topics, item.submission.topic)) {
    enrichment.topic = values.topics;
  }

  if (!sameSet(values.locations, item.submission.location)) {
    enrichment.location = values.locations;
  }

  if (!sameSet(values.coreRisks, item.submission.core_risk)) {
    enrichment.core_risk = values.coreRisks;
  }

  if (Object.keys(enrichment).length > 0) {
    payload.enrichment = enrichment;
  }

  return payload;
}
